import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Party } from '../../models/party.model';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { OtpChannelEnum } from '../../enums/otp-channel.enum';
import {
	VerifyOtpRequest,
	SendOtpRequest,
	PostPartyRequest,
	RequestOtpResponse,
	GenericPartyResponse,
	PatchPartyRequest,
	GetPartyResponse
} from '../../models/api.model';
import { PARTY_SERVICE_API_ROUTES } from './api-routes';
import { DetailLevelEnum } from '@shared/enums/detail-level.enum';

/**
 * PartyApiService provides an interface to the PartyApiService Api, and a store of Party data.
 * Most of this service could be generated by an openapi document.
 */
@Injectable({
	providedIn: 'root'
})
export class PartyApiService {
	constructor(private http: HttpClient) {}

	public postParty(
		party: Party,
		flowParam?: string,
		retailPartyId? : string
	): Observable<GenericPartyResponse> {
		const payload: PostPartyRequest = party;
		let params = new HttpParams();
		if (flowParam) {
			params = new HttpParams().set('flow', flowParam);
		}
		let headers = new HttpHeaders();
		if(retailPartyId) {
			headers = headers.set('retailPartyId', retailPartyId);
		}
		const requestOptions = { headers, params };
		// eslint-disable-next-line max-len
		// TODO: consider adding a base service class like: https://dev.azure.com/EQDevOps/DigitalBank/_git/web-redesign?version=GBepic/bau-codebase&_a=contents&path=/angular/src/app/core/services/base.service.ts
		return this.http.post<GenericPartyResponse>(
			PARTY_SERVICE_API_ROUTES.postParty(),
			payload,
			requestOptions
		);
	}
	public requestOtp(
		email: string,
		partyId: string,
		mobile?: boolean,
		phoneNumber?: string
	): Observable<RequestOtpResponse> {
		const payload: SendOtpRequest = {
			email,
			otpChannel: mobile ? OtpChannelEnum.MOBILE : OtpChannelEnum.EMAIL,
			phoneNumber
		};
		return this.http.post<RequestOtpResponse>(
			PARTY_SERVICE_API_ROUTES.otp(partyId),
			payload
		);
	}
	public verifyOtp(
		pin: string,
		email: string,
		partyId: string
	): Observable<RequestOtpResponse> {
		const payload: VerifyOtpRequest = {
			email,
			pin
		};
		return this.http.put<RequestOtpResponse>(
			PARTY_SERVICE_API_ROUTES.otp(partyId),
			payload
		);
	}

	public patchParty(
		payload: PatchPartyRequest,
		partyId: Required<Party>['partyId']
	): Observable<GenericPartyResponse> {
		return this.http.patch<GenericPartyResponse>(
			PARTY_SERVICE_API_ROUTES.patchParty(partyId),
			payload
		);
	}

	/**
	 * Method to request all Party information
	 *
	 * @param partyId - Party identifier
	 * @param detailLevel - Level of details returned by the API
	 * @returns Party Object
	 */
	public getParty(
		partyId: string,
		detailLevel: DetailLevelEnum
	): Observable<Party | GetPartyResponse> {
		const requestOptions = {
			params: new HttpParams().set('detailLevel', detailLevel)
		};

		return this.http.get<Party | GetPartyResponse>(
			PARTY_SERVICE_API_ROUTES.getParty(partyId),
			requestOptions
		);
	}

	public migrateT24Profile(
		email: string,
		accessToken: string
	): Observable<GenericPartyResponse> {
		const requestOptions = {
			headers: new HttpHeaders()
				.set('email', email)
				.set('accessToken', accessToken)
		};
		return this.http.post<GenericPartyResponse>(
			PARTY_SERVICE_API_ROUTES.migrateT24Profile(),
			{},
			requestOptions
		);
	}
}
